import React from "react";
import { Database, Activity, ShieldCheck, Cpu, Bot, FileText, Boxes, Terminal } from "lucide-react";
import { useLang } from "../context/LangContext";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";

export default function Synapse() {
  const { t } = useLang();
  const capabilities = [
    { Icon: Activity, h: t.synapse.cap_1_h, d: t.synapse.cap_1_d, code: "01" },
    { Icon: Database, h: t.synapse.cap_2_h, d: t.synapse.cap_2_d, code: "02" },
    { Icon: ShieldCheck, h: t.synapse.cap_3_h, d: t.synapse.cap_3_d, code: "03" },
    { Icon: Cpu, h: t.synapse.cap_4_h, d: t.synapse.cap_4_d, code: "04" },
  ];
  const catalog = [
    { Icon: Boxes, h: t.synapse.cat_1_h, d: t.synapse.cat_1_d },
    { Icon: Bot, h: t.synapse.cat_2_h, d: t.synapse.cat_2_d },
    { Icon: FileText, h: t.synapse.cat_3_h, d: t.synapse.cat_3_d },
  ];

  return (
    <div data-testid="page-synapse">
      {/* HERO with grid */}
      <section className="relative section-pad synapse-grid overflow-hidden">
        <div className="page-container relative">
          <FadeUp>
            <div className="flex items-center gap-3 mb-6">
              <Terminal className="w-4 h-4 text-cerulean" strokeWidth={1.5} />
              <p className="text-[11px] font-mono tracking-[0.22em] uppercase text-cerulean">{t.synapse.eyebrow}</p>
            </div>
            <h1 className="synapse-display text-4xl md:text-5xl lg:text-6xl max-w-4xl leading-[1.06]">
              {t.synapse.title}
            </h1>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-10 text-base md:text-lg text-text-muted leading-relaxed max-w-3xl">{t.synapse.lead}</p>
          </FadeUp>
          <FadeUp delay={0.18}>
            <p className="mt-6 text-base md:text-lg text-text-muted leading-relaxed max-w-3xl">{t.synapse.lead_2}</p>
          </FadeUp>

          {/* Terminal-style block */}
          <FadeUp delay={0.24}>
            <div className="mt-16 max-w-2xl border border-rule bg-white">
              <div className="border-b border-rule px-4 py-2.5 flex items-center gap-2 bg-pearl">
                <span className="w-2.5 h-2.5 rounded-full bg-cerulean/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-cerulean/40" />
                <span className="w-2.5 h-2.5 rounded-full bg-cerulean/20" />
                <span className="ml-3 text-[10px] font-mono tracking-widest text-text-muted">synapse@convergen ~ %</span>
              </div>
              <div className="px-5 py-5 font-mono text-[12.5px] text-ink leading-7">
                <div><span className="text-cerulean">→</span> ingest --source ngo_datasets/*.csv</div>
                <div><span className="text-cerulean">→</span> validate --schema rwe.standard</div>
                <div><span className="text-cerulean">→</span> structure --output dossier.pdf</div>
                <div className="text-text-muted">[ok] 1.2M records normalized · evidence ready</div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <div className="page-container"><div className="h-px bg-rule" /></div>

      {/* CAPABILITIES */}
      <section className="section-pad">
        <div className="page-container">
          <FadeUp>
            <p className="text-[11px] font-mono tracking-[0.22em] text-cerulean">// CAPABILITIES</p>
            <h2 className="synapse-display text-3xl md:text-5xl mt-4 max-w-3xl">{t.synapse.capabilities_title}</h2>
            <p className="mt-8 text-text-muted text-base md:text-lg max-w-2xl leading-relaxed">{t.synapse.capabilities_sub}</p>
          </FadeUp>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-0 border border-rule">
            {capabilities.map((c, i) => (
              <StaggerItem key={i}>
                <div className={`p-8 md:p-10 h-full bg-white ${i < 2 ? "border-b" : ""} ${i % 2 === 0 ? "md:border-r" : ""} border-rule mosaic-card`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-mono tracking-[0.22em] text-cerulean">/{c.code}</span>
                    <c.Icon className="w-6 h-6 text-ink" strokeWidth={1.3} />
                  </div>
                  <h3 className="mt-10 synapse-display text-xl md:text-2xl">{c.h}</h3>
                  <p className="mt-5 text-sm text-text-muted leading-relaxed">{c.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* IMPACT */}
      <section className="section-pad bg-ink text-white">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-5">
            <p className="text-[11px] font-mono tracking-[0.22em] text-cerulean">// IMPACT</p>
            <h2 className="synapse-display text-white text-3xl md:text-5xl mt-4">{t.synapse.impact_title}</h2>
            <p className="mt-6 text-white/60 text-sm font-mono tracking-widest uppercase">{t.synapse.impact_sub}</p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7">
            <p className="text-white/80 leading-relaxed text-base md:text-lg">{t.synapse.impact_p}</p>
          </FadeUp>
        </div>
      </section>

      {/* CATALOG */}
      <section className="section-pad">
        <div className="page-container">
          <FadeUp>
            <p className="text-[11px] font-mono tracking-[0.22em] text-cerulean">// CATALOG</p>
            <h2 className="synapse-display text-3xl md:text-5xl mt-4 max-w-3xl">{t.synapse.catalog_title}</h2>
          </FadeUp>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {catalog.map((c, i) => (
              <StaggerItem key={i}>
                <div className="border border-rule p-8 h-full bg-white mosaic-card">
                  <c.Icon className="w-7 h-7 text-cerulean" strokeWidth={1.3} />
                  <div className="mt-6 text-[10px] font-mono tracking-[0.22em] text-cerulean">SERVICE_{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-3 synapse-display text-xl">{c.h}</h3>
                  <p className="mt-5 text-sm text-text-muted leading-relaxed">{c.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
